import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { Spinner } from 'src/common/components/Spinner';
import { Modal } from 'src/common/components/Modal';
import { Button } from 'src/common/components/buttons/Button';
import newOwnerIllu from 'src/assets/illu_new-owner.svg';
import { SelectNative } from 'src/common/components/inputs/SelectNative/SelectNative';
import toast from 'src/common/toast';
import { useProjectUsersQuery } from '../queries';
import { transferProjectOwnership } from '../api/projectsApi';

interface Props {
  projectId: string;
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onTransferred?: () => void;
}

export const TransferOwnerModal = ({
  projectId,
  userId,
  isOpen,
  onClose,
  onTransferred,
}: Props) => {
  const { t } = useTranslation(['project', 'common']);
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<{ newOwner: string }>({ defaultValues: { newOwner: '' } });

  const { data: projectMembers, isPending } = useProjectUsersQuery(projectId);

  const handleTransfer = async ({ newOwner }: { newOwner: string }) => {
    try {
      await transferProjectOwnership(projectId, newOwner);
      queryClient.invalidateQueries({
        queryKey: ['projects'],
      });
      toast.success(t('project:transferOwner.success'));
      onTransferred && onTransferred();
      onClose();
    } catch (e) {
      toast.error(t('project:transferOwner.failure'));
    }
  };

  const potentialNewOwners = projectMembers.filter(
    (u) => u._id !== userId && u.isInProjectCompany,
  );

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="w-[680px]">
      <Modal.Header onClose={onClose}>
        {t('project:transferOwner.title')}
      </Modal.Header>
      <Modal.Body>
        <form
          className="mt-8 flex flex-col items-center gap-6"
          onSubmit={handleSubmit(handleTransfer)}
        >
          <div className="ml-4 flex gap-9">
            <img src={newOwnerIllu} alt="" />
            <div className="flex w-full flex-col gap-4">
              <p className="text-shuttleGray-600">
                <Trans ns="project" i18nKey="transferOwner.text" />
              </p>
              {isPending ? (
                <Spinner />
              ) : (
                <SelectNative
                  label={t('project:transferOwner.newOwner')}
                  required
                  {...register('newOwner')}
                >
                  <option value="" disabled hidden>
                    {t('project:transferOwner.choose')}
                  </option>
                  {potentialNewOwners.map((member) => (
                    <option key={member._id} value={member._id}>
                      {member.name} {member.surname}
                    </option>
                  ))}
                </SelectNative>
              )}
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="tertiary" onClick={onClose}>
              {t('common:button.abort')}
            </Button>
            <Button variant="primary" type="submit" disabled={!isDirty}>
              {t('common:button.save')}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
